import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Store, MapPin, ExternalLink, Trophy, Loader2, CreditCard, QrCode, Zap, ArrowRight, Landmark, Gift } from "lucide-react";
import QrPosterModal from "@/components/QrPosterModal";
import SponsorValueTable from "@/components/SponsorValueTable";
import { prizeMeta } from "@/components/PrizeDisplay";
import MysteryInfo from "@/components/MysteryInfo";

function winnerPrizeLabel(w) {
  if (w?.is_mystery) return { text: "Mystery Loot", icon: "🎁", color: "#D7A62A" };
  const pt = w?.prize_type || "cash";
  if (pt === "cash") {
    return { text: w?.hide_amount ? "CASH" : `$${w?.amount ?? 0}`, color: "#76B82A" };
  }
  const meta = prizeMeta(w);
  return { text: meta.label, color: meta.color };
}

export function SponsorsPage() {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/sponsors");
      setSponsors(data.sponsors || []);
    })();
  }, []);

  return (
    <div className="py-8 space-y-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline">Powered by locals</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
            Our <span style={{ color: "#D7A62A" }}>sponsors</span>
          </h1>
        </div>
        <Link to="/sponsor-apply" className="btn-loot inline-block" data-testid="sponsor-apply-link">
          Sponsor a drop →
        </Link>
      </div>

      {sponsors.length === 0 ? (
        <div className="loot-card-flat p-12 text-center text-[#F5F2E8]/60">No sponsors yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sponsors.map((s) => (
            <Link
              to={`/sponsors/${s.id}`}
              key={s.id}
              className="loot-card p-6 block"
              data-testid={`sponsor-card-${s.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 border border-[#F5F2E8]/20 flex items-center justify-center" style={{ background: "#123A1A" }}>
                  <Store className="text-[#76B82A]" />
                </div>
                <div>
                  <div className="font-display uppercase text-xl leading-tight">{s.business_name}</div>
                  {s.city && (
                    <div className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1 mt-1">
                      <MapPin size={12} /> {s.city}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm text-[#F5F2E8]/70 line-clamp-3">{s.description || "Proud supporter of The Local Loot."}</p>
              <div className="mt-4 flex gap-3 text-xs font-mono text-[#F5F2E8]/60">
                <span>{s.drops_count} drops</span>
                <span>{s.winners_count} winners</span>
                <span className="uppercase text-[#D7A62A]">{s.package.replace("_", " ")}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* ============================================================
           VALUE PROPOSITION: Why sponsor a Cash Drop, Loot Quest, or Branded Waymark
           ============================================================ */}
      <section className="space-y-6 pt-4" data-testid="sponsor-value-section">
        <div>
          <div className="overline" style={{ color: "#D7A62A" }}>Why sponsor</div>
          <h2 className="font-display uppercase text-4xl md:text-5xl tracking-tight mt-2 leading-[0.95]">
            Stop Buying <span style={{ color: "rgba(245,242,232,0.3)", textDecoration: "line-through" }}>Impressions</span>.
            <br />
            Start Funding <span style={{ color: "#76B82A" }}>Moments</span>.
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#F5F2E8]/75 max-w-3xl leading-relaxed">
            Three ways to put your storefront on the map: a{" "}
            <strong style={{ color: "#E53935" }}>Cash Drop</strong> (loud, public, one-day),{" "}
            a <strong style={{ color: "#D7A62A" }}>Loot Quest</strong> (slow-burn, multi-day, TikTok-ready),{" "}
            or a <strong style={{ color: "#76B82A" }}>Branded Waymark</strong>{" "}
            (permanent pin on the city map with a coupon perk — pays for itself in walk-ins).
            You're not buying a scrollable ad. You're planting a story in a real neighborhood,
            and every hunter who chases it becomes a customer with a selfie.
          </p>
        </div>

        {/* Comparison table */}
        <SponsorValueTable />

        {/* Three-up: Cash Drop · Loot Quest · Branded Waymark */}
        <div className="grid md:grid-cols-3 gap-4 pt-2">
          <div
            className="loot-card p-6 space-y-3"
            style={{ borderColor: "#E53935" }}
            data-testid="sponsor-cash-card"
          >
            <div className="flex items-center gap-2">
              <Zap size={18} style={{ color: "#E53935" }} />
              <div className="overline" style={{ color: "#E53935" }}>Cash Drop · timed hunt</div>
            </div>
            <h3 className="font-display uppercase text-2xl md:text-3xl">Fast. Public. Loud.</h3>
            <p className="text-sm md:text-base text-[#F5F2E8]/75 leading-relaxed">
              A physical envelope of cash hidden in your neighborhood. Clues drop on a schedule,
              every hunter in town gets the push notification, and the first finder takes the prize —
              usually within the hour. Your business name rides every clue, every leaderboard entry,
              and every selfie on the Winners Wall.
            </p>
            <ul className="font-mono text-xs text-[#F5F2E8]/70 space-y-1 pt-2 border-t border-[#F5F2E8]/10">
              <li>→ Instant push to every hunter in the city</li>
              <li>→ Name-checked on the leaderboard + winners wall</li>
              <li>→ Printable QR poster for your shop window</li>
            </ul>
          </div>

          <div
            className="loot-card p-6 space-y-3"
            style={{ borderColor: "#D7A62A" }}
            data-testid="sponsor-quest-card"
          >
            <div className="flex items-center gap-2">
              <Trophy size={18} style={{ color: "#D7A62A" }} />
              <div className="overline" style={{ color: "#D7A62A" }}>Loot Quest · slow burn</div>
            </div>
            <h3 className="font-display uppercase text-2xl md:text-3xl">Mystery. Days. Obsessed.</h3>
            <p className="text-sm md:text-base text-[#F5F2E8]/75 leading-relaxed">
              A physical treasure chest hidden for days or weeks. Clues release over time, warmer/colder
              GPS hints kick in, and the hunt builds its own drama online — TikTok videos, "have you
              found it yet?" group chats, news-tip texts. When it's finally cracked, the winner films
              the reveal with your brand on the chest.
            </p>
            <ul className="font-mono text-xs text-[#F5F2E8]/70 space-y-1 pt-2 border-t border-[#F5F2E8]/10">
              <li>→ Multi-day buzz — not a 1-hour blip</li>
              <li>→ User-generated content compounds daily</li>
              <li>→ Prize can be cash, gift card, merch, or branded</li>
            </ul>
          </div>

          {/* Branded Waymark — the low-cost recurring tier */}
          <div
            className="loot-card p-6 space-y-3"
            style={{ borderColor: "#76B82A" }}
            data-testid="sponsor-waymark-card"
          >
            <div className="flex items-center gap-2">
              <Landmark size={18} style={{ color: "#76B82A" }} />
              <div className="overline" style={{ color: "#76B82A" }}>Branded Waymark · always on</div>
            </div>
            <h3 className="font-display uppercase text-2xl md:text-3xl">Permanent. Local. Pays you back.</h3>
            <p className="text-sm md:text-base text-[#F5F2E8]/75 leading-relaxed">
              A permanent pin on the city map — at your front door, your patio, your most photogenic
              corner. Hunters walking by unlock a short story about your spot, earn a Passport stamp,
              and reveal <strong style={{ color: "#76B82A" }}>your coupon</strong> right in the app
              (10% off, free drip, BOGO — you decide). You aren't giving away cash. You're giving away
              a discount to a customer who is standing on your sidewalk.
            </p>
            <ul className="font-mono text-xs text-[#F5F2E8]/70 space-y-1 pt-2 border-t border-[#F5F2E8]/10">
              <li>→ <Gift size={10} className="inline mb-[2px]" style={{ color: "#76B82A" }} /> Coupon unlocks when the hunter is at your door</li>
              <li>→ Recurring $50/mo — easiest yes in the lineup</li>
              <li>→ Auto-featured in the map, the Passport, and Waymarks nav</li>
              <li>→ You keep editing the perk anytime from the sponsor portal</li>
            </ul>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className="loot-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mt-2"
          style={{
            borderColor: "#D7A62A",
            background: "linear-gradient(135deg, rgba(215,166,42,0.08), rgba(15,15,15,1) 60%)",
          }}
        >
          <div>
            <div className="font-display uppercase text-2xl md:text-3xl">
              Ready to put your brand where the action is?
            </div>
            <div className="font-mono text-xs text-[#F5F2E8]/60 mt-2 uppercase tracking-wider">
              Takes 2 minutes · We reply same-day
            </div>
          </div>
          <Link to="/sponsor-apply" className="btn-loot inline-flex items-center gap-2 shrink-0" data-testid="sponsor-value-cta">
            Sponsor a Drop <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

export function SponsorProfilePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [posterCity, setPosterCity] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await api.get(`/sponsors/${id}`);
      setData(res.data);
    })();
  }, [id]);
  if (!data) return <div className="py-20 text-center text-[#F5F2E8]/60">Loading...</div>;
  const { sponsor, drops, winners } = data;

  // Distinct cities the sponsor has supported (fallback to profile city)
  const dropCities = Array.from(new Set(drops.map((d) => d.city).filter(Boolean)));
  const posterCities = dropCities.length > 0 ? dropCities : (sponsor.city ? [sponsor.city] : []);

  return (
    <div className="py-8 space-y-8">
      <Link to="/sponsors" className="font-mono text-xs text-[#F5F2E8]/50">← Sponsors</Link>
      <div className="loot-card-flat p-8">
        <div className="flex flex-col md:flex-row gap-6 md:items-center">
          <div className="w-24 h-24 border border-[#F5F2E8]/20 flex items-center justify-center" style={{ background: "#123A1A" }}>
            <Store size={40} className="text-[#76B82A]" />
          </div>
          <div className="flex-1">
            <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">
              {sponsor.business_name}
            </h1>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-[#F5F2E8]/70">
              {sponsor.city && <span className="flex items-center gap-1"><MapPin size={14} /> {sponsor.city}</span>}
              {sponsor.website && (
                <a href={sponsor.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#D7A62A]">
                  <ExternalLink size={14} /> Website
                </a>
              )}
            </div>
            <p className="mt-3 text-[#F5F2E8]/80">{sponsor.description}</p>
            <div className="mt-3 tag-pill inline-block" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
              Proud sponsor of The Local Loot
            </div>
          </div>
        </div>
      </div>

      {/* QR Posters section */}
      {posterCities.length > 0 && (
        <section className="loot-card-flat p-6" style={{ borderColor: "#D7A62A" }}>
          <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
            <div className="flex-1">
              <div className="overline flex items-center gap-2">
                <QrCode size={12} /> Sponsor perk
              </div>
              <h2 className="font-display uppercase text-2xl md:text-3xl mt-2 tracking-tight">
                Download your <span style={{ color: "#D7A62A" }}>QR posters</span>
              </h2>
              <p className="text-sm text-[#F5F2E8]/70 mt-2 max-w-lg">
                Printable 8.5×11 letter-size posters with a QR that deep-links to your city's hunt page. Hang them in your storefront, window, or on community boards — anyone who scans lands on a branded page featuring your logo.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {posterCities.map((c) => (
              <button
                key={c}
                onClick={() => setPosterCity(c)}
                className="btn-gold text-xs inline-flex items-center gap-2"
                data-testid={`sponsor-qr-${c}`}
              >
                <QrCode size={14} /> Poster for {c}
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="overline mb-3">Drops sponsored</div>
        <div className="grid md:grid-cols-3 gap-4">
          {drops.length === 0 && <div className="text-[#F5F2E8]/50">None yet.</div>}
          {drops.map((d) => (
            <Link key={d.id} to={`/drops/${d.id}`} className="loot-card p-4 block">
              <div className="tag-pill inline-block">{d.status}</div>
              <div className="font-display uppercase text-lg mt-2">{d.title}</div>
              <div className="text-sm text-[#F5F2E8]/60">{d.city}</div>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="overline mb-3">Winners from their drops</div>
        <div className="grid md:grid-cols-3 gap-4">
          {winners.length === 0 && <div className="text-[#F5F2E8]/50">None yet.</div>}
          {winners.map((w) => {
            const prize = winnerPrizeLabel(w);
            return (
              <div key={w.id} className="loot-card-flat p-4" data-testid={`sponsor-winner-${w.id}`}>
                <Trophy className="text-[#D7A62A]" size={18} />
                <div className="font-display uppercase text-lg mt-2">@{w.username || w.name}</div>
                <div className="text-sm text-[#F5F2E8]/60 flex items-center gap-1 flex-wrap">
                  <span style={{ color: prize.color }} className="font-display uppercase">
                    {prize.icon ? `${prize.icon} ` : ""}Found {prize.text}
                  </span>
                  {w.is_mystery && <MysteryInfo size={12} />}
                  <span className="text-[#F5F2E8]/40">·</span>
                  <span>{w.city}</span>
                </div>
                {w.prize_custom_label && !w.is_mystery && (
                  <div className="font-mono text-[10px] text-[#F5F2E8]/55 mt-1">{w.prize_custom_label}</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <QrPosterModal
        open={Boolean(posterCity)}
        city={posterCity || ""}
        sponsorName={sponsor.business_name}
        sponsorId={sponsor.id}
        sponsorLogoUrl={sponsor.logo_url || null}
        onClose={() => setPosterCity(null)}
      />
    </div>
  );
}

export function SponsorApplyPage() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("featured");
  const [inquiryType, setInquiryType] = useState("drop"); // "drop" | "waymark"
  const [now, setNow] = useState(Date.now());
  const fmtPrice = (n) => {
    const v = Number(n || 0);
    return v % 1 === 0 ? v.toFixed(0) : v.toFixed(2);
  };
  // Refresh countdown + promo-active flag every minute
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const countdownLabel = (iso) => {
    if (!iso) return null;
    const diff = new Date(iso).getTime() - now;
    if (diff <= 0) return null;
    const d = Math.floor(diff / 86_400_000);
    const h = Math.floor((diff % 86_400_000) / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    if (d > 0) return `Ends in ${d}d ${h}h`;
    if (h > 0) return `Ends in ${h}h ${m}m`;
    return `Ends in ${m}m`;
  };
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "", website: "",
    city: "", description: "",
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/payments/packages");
      setPackages(data.packages || []);
    })();
    (async () => {
      try {
        const { data } = await api.get("/payments/status");
        setCheckoutEnabled(Boolean(data?.checkout_enabled));
      } catch {
        setCheckoutEnabled(true);
      }
    })();
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedPkg = packages.find((p) => p.id === selected);

  const validate = () => {
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.email.trim() || !form.city.trim()) {
      toast.error("Business name, contact, email and city are required.");
      return false;
    }
    return true;
  };

  const checkout = async () => {
    if (!validate()) return;
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: selected,
        origin_url: window.location.origin,
        ...form,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout failed");
        setCheckoutLoading(false);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setCheckoutLoading(false);
    }
  };

  const submitInquiry = async () => {
    if (!validate()) return;
    setInquiryLoading(true);
    try {
      await api.post("/sponsor-applications", {
        ...form,
        package_interest: inquiryType === "waymark" ? "waymark" : selected,
      });
      setDone(true);
      toast.success(
        inquiryType === "waymark"
          ? "Waymark inquiry submitted. We'll reach out same-day."
          : "Inquiry submitted. We'll reach out.",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setInquiryLoading(false);
    }
  };

  if (done) {
    return (
      <div className="py-20 max-w-md mx-auto text-center">
        <h2 className="font-display uppercase text-4xl">You're in the loop.</h2>
        <p className="mt-4 text-[#F5F2E8]/70">We'll be in touch about your drop sponsorship.</p>
        <Link to="/home" className="btn-loot mt-6 inline-block">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="py-8 max-w-3xl mx-auto space-y-8">
      <div>
        <div className="overline">Local advertising that actually performs</div>
        <h1 className="font-display uppercase text-5xl tracking-tight mt-2">
          Sponsor a <span style={{ color: "#D7A62A" }}>drop</span> or{" "}
          <span style={{ color: "#76B82A" }}>waymark</span>
        </h1>
        <p className="text-[#F5F2E8]/70 mt-3">
          Four options — three one-shot tiers below, plus a $50/mo permanent
          Waymark with your coupon built in. All built to drive real faces through
          your door.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {packages.map((p) => {
          const active = selected === p.id;
          const promo = p.promo_active;
          const effective = promo ? p.promo_amount : p.amount;
          const countdown = promo ? countdownLabel(p.promo_expires_at) : null;
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => setSelected(p.id)}
              data-testid={`tier-${p.id}`}
              className={`loot-card p-5 text-left relative ${active ? "border-[#76B82A]" : ""}`}
              style={{
                background: active ? "rgba(118,184,42,0.06)" : "#0F0F0F",
                borderColor: promo ? "#E53935" : undefined,
              }}
            >
              {promo && (
                <span
                  className="absolute -top-2 left-3 tag-pill inline-flex items-center gap-1"
                  style={{ background: "#E53935", color: "#F5F2E8", borderColor: "#E53935" }}
                  data-testid={`tier-promo-${p.id}`}
                >
                  <Zap size={10} /> {p.promo_label || "Limited-time"}
                </span>
              )}
              {p.highlight && !promo && (
                <span
                  className="absolute -top-2 right-3 tag-pill"
                  style={{ background: "#D7A62A", color: "#0F0F0F", borderColor: "#D7A62A" }}
                >
                  Most popular
                </span>
              )}
              <div className="font-display uppercase text-2xl mt-1">{p.label}</div>
              {promo ? (
                <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                  <span
                    className="font-display text-4xl"
                    style={{ color: "#E53935" }}
                    data-testid={`tier-price-${p.id}`}
                  >
                    ${fmtPrice(effective)}
                  </span>
                  <span
                    className="font-mono text-lg text-[#F5F2E8]/40 line-through"
                    data-testid={`tier-basestrike-${p.id}`}
                  >
                    ${fmtPrice(p.amount)}
                  </span>
                </div>
              ) : (
                <div
                  className="font-display text-4xl mt-2"
                  style={{ color: "#D7A62A" }}
                  data-testid={`tier-price-${p.id}`}
                >
                  ${fmtPrice(effective)}
                </div>
              )}
              {countdown && (
                <div
                  className="font-mono text-[10px] uppercase tracking-widest mt-1"
                  style={{ color: "#E53935" }}
                  data-testid={`tier-countdown-${p.id}`}
                >
                  {countdown}
                </div>
              )}
              {p.description && (
                <p className="mt-2 text-xs font-mono text-[#F5F2E8]/50 leading-snug">
                  {p.description}
                </p>
              )}
              <ul className="mt-3 space-y-1 text-sm text-[#F5F2E8]/70">
                {(p.perks || []).map((pk) => (
                  <li key={pk} className="flex items-start gap-2">
                    <span className="text-[#76B82A]">●</span> {pk}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Branded Waymark — inquiry-only recurring tier */}
      <button
        type="button"
        onClick={() => setInquiryType((t) => (t === "waymark" ? "drop" : "waymark"))}
        className="loot-card p-5 md:p-6 w-full text-left transition"
        style={{
          borderColor: inquiryType === "waymark" ? "#76B82A" : "rgba(118,184,42,0.5)",
          background: inquiryType === "waymark"
            ? "linear-gradient(135deg, rgba(118,184,42,0.18), rgba(15,15,15,1) 60%)"
            : "linear-gradient(135deg, rgba(118,184,42,0.08), rgba(15,15,15,1) 60%)",
          borderWidth: inquiryType === "waymark" ? "2px" : "1px",
        }}
        data-testid="waymark-inquiry-card"
      >
        <div className="flex items-start gap-3">
          <Landmark size={22} style={{ color: "#76B82A" }} className="shrink-0 mt-1" />
          <div className="flex-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="font-display uppercase text-2xl md:text-3xl">
                {inquiryType === "waymark" ? (
                  <>
                    <span style={{ color: "#76B82A" }}>✓</span> Asking about a{" "}
                    <span style={{ color: "#76B82A" }}>Branded Waymark</span>
                  </>
                ) : (
                  <>
                    Prefer a <span style={{ color: "#76B82A" }}>Branded Waymark</span>?
                  </>
                )}
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-[#76B82A]">
                $50/mo · always-on
              </div>
            </div>
            <p className="text-sm md:text-base text-[#F5F2E8]/80 mt-2 leading-relaxed">
              Permanent pin at your door. Hunters walking by unlock your coupon.
              Perfect for cafes, shops, and anyone with a storefront. We provision it
              manually after your application — takes a day, no Stripe checkout required.
            </p>
            <p className="font-mono text-[11px] text-[#F5F2E8]/60 mt-2">
              {inquiryType === "waymark"
                ? "This form is now a Waymark inquiry — the tier buttons above are ignored. Tap again to switch back."
                : "Tap this box to send a Waymark inquiry instead of a drop tier."}
            </p>
          </div>
        </div>
      </button>

      {!checkoutEnabled && (
        <div
          className="loot-card-flat p-4 flex items-start gap-3"
          style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.08)" }}
          data-testid="sa-checkout-disabled-banner"
        >
          <CreditCard size={16} className="text-[#D7A62A] mt-0.5 shrink-0" />
          <div className="text-sm text-[#F5F2E8]/85">
            <strong className="text-[#D7A62A]">Online payment temporarily off.</strong> Drop your details below and we'll reach out to lock in your sponsorship personally. Same packages, same perks — just a quick invoice instead of checkout.
          </div>
        </div>
      )}

      <div className="loot-card-flat p-6 space-y-3" data-testid="sponsor-form">
        <div className="overline">Business details</div>
        <input className="loot-input" placeholder="Business name" required value={form.business_name} onChange={onChange("business_name")} data-testid="sa-business" />
        <input className="loot-input" placeholder="Contact name" required value={form.contact_name} onChange={onChange("contact_name")} data-testid="sa-contact" />
        <div className="grid md:grid-cols-2 gap-3">
          <input className="loot-input" placeholder="Email" type="email" required value={form.email} onChange={onChange("email")} data-testid="sa-email" />
          <input className="loot-input" placeholder="Phone (optional)" value={form.phone} onChange={onChange("phone")} data-testid="sa-phone" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <input className="loot-input" placeholder="Website / social" value={form.website} onChange={onChange("website")} data-testid="sa-website" />
          <input className="loot-input" placeholder="City" required value={form.city} onChange={onChange("city")} data-testid="sa-city" />
        </div>
        <textarea rows={3} className="loot-input" placeholder="Short description of your business (shown on sponsor profile)" value={form.description} onChange={onChange("description")} data-testid="sa-desc" />
      </div>

      <div className="loot-card-flat p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            {(() => {
              // Show the Waymark summary when the visitor explicitly tapped the
              // Waymark inquiry card above, OR mentioned "waymark" in the description.
              const desc = (form.description || "").toLowerCase();
              const interestedInWaymark = inquiryType === "waymark" || desc.includes("waymark");

              if (interestedInWaymark) {
                return (
                  <>
                    <div className="overline" style={{ color: "#76B82A" }}>Branded Waymark inquiry</div>
                    <div className="flex items-baseline gap-2 mt-1 flex-wrap">
                      <div className="font-display text-5xl" style={{ color: "#76B82A" }}>
                        $50<span className="text-xl text-[#F5F2E8]/60">/mo</span>
                      </div>
                      <div className="font-mono text-xs text-[#F5F2E8]/60 uppercase tracking-widest">
                        always-on · no checkout today
                      </div>
                    </div>
                    <div className="text-xs font-mono text-[#F5F2E8]/50 mt-1">
                      You won't be charged by this form. We'll email you to confirm details and invoice separately.
                    </div>
                  </>
                );
              }

              const summaryLabel = checkoutEnabled ? "Total due today" : "Package price";
              return (
                <>
                  <div className="overline">{summaryLabel}</div>
                  {selectedPkg?.promo_active ? (
                    <>
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <div className="font-display text-5xl" style={{ color: "#E53935" }} data-testid="sa-effective-price">
                          ${fmtPrice(selectedPkg.promo_amount)}
                        </div>
                        <div className="font-mono text-xl text-[#F5F2E8]/40 line-through">
                          ${fmtPrice(selectedPkg.amount)}
                        </div>
                      </div>
                      <div className="text-xs font-mono text-[#E53935] mt-1 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Zap size={10} /> {selectedPkg.promo_label || "Promo"}
                        </span>
                        <span>
                          You save ${fmtPrice(selectedPkg.amount - selectedPkg.promo_amount)}
                        </span>
                        {countdownLabel(selectedPkg.promo_expires_at) && (
                          <span>· {countdownLabel(selectedPkg.promo_expires_at)}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="font-display text-5xl" style={{ color: "#D7A62A" }} data-testid="sa-effective-price">
                      ${selectedPkg ? fmtPrice(selectedPkg.amount) : "—"}
                    </div>
                  )}
                  <div className="text-xs font-mono text-[#F5F2E8]/50 mt-1">
                    {checkoutEnabled
                      ? "One-time payment · secure Stripe checkout"
                      : "No charge today — inquiry only. We'll follow up to confirm."}
                  </div>
                </>
              );
            })()}
          </div>
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            {checkoutEnabled && inquiryType !== "waymark" ? (
              <>
                <button
                  type="button"
                  onClick={checkout}
                  disabled={checkoutLoading || !selectedPkg}
                  className="btn-loot flex items-center gap-2 justify-center"
                  data-testid="sa-checkout-btn"
                >
                  {checkoutLoading ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  Pay & go live
                </button>
                <button
                  type="button"
                  onClick={submitInquiry}
                  disabled={inquiryLoading}
                  className="btn-ghost"
                  data-testid="sa-inquiry-btn"
                >
                  {inquiryLoading ? "..." : "Submit inquiry instead"}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={submitInquiry}
                disabled={inquiryLoading}
                className="btn-loot flex items-center gap-2 justify-center"
                data-testid="sa-inquiry-btn"
              >
                {inquiryLoading ? "..." : (inquiryType === "waymark" ? "Send Waymark inquiry" : "Submit inquiry")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
